import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IonContent, IonHeader, IonPage, IonRefresher, IonRefresherContent, useIonViewDidEnter, useIonViewWillEnter } from "@ionic/react";
import "./Tab1.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useScanner } from "../../context/ScannerContext";
import { useTheme } from "@emotion/react";
import CurrentWorkingList from "../../components/CurrentWorkingList/CurrentWorkingList";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { useUserData } from "../../context/UserDataFetcher";
import ShipmentViewAppBar from "../../components/AppBar/ShipmentViewAppBar";
import { useDeveloperMode } from "../../context/DeveloperModeContext";
import SignatureDevFooter from "../../components/SignatureActions/SignatureDevFooter";
import SignatureCaptureModal from "../../components/SignatureActions/SignatureCaptureModal";
import type { SignatureRole, SignatureCaptureResult } from "../../components/SignatureActions/types";
import { useUserControls } from "../../context/UserControlsProvider";
import { isVerboseLoggingEnabled } from "../../utils/debug";

const Tab1: React.FC = () => {
  const { stopScanner, isScannerActive } = useScanner();
  const theme: any = useTheme();
  const userData = useUserData();
  const { fetchOrdersByDealerId, fetchShipmentsByManufacturerId, fetchShipmentsForDriver } = usePrimaryFetchedData();
  const { developerMode, signatureOverrideActive } = useDeveloperMode();
  const { showSnackbar, processedGroups, unprocessedGroups } = useUserControls();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureRole, setCaptureRole] = useState<SignatureRole | null>(null);
  const verbose = useMemo(() => isVerboseLoggingEnabled(), []);

  const logSignatureDebug = useCallback(
    (event: string, meta: Record<string, unknown> = {}) => {
      if (!verbose) {
        return;
      }
      // eslint-disable-next-line no-console
      console.debug("[Tab1 SignatureDebug]", event, {
        developerMode,
        captureOpen,
        captureRole,
        ...meta,
      });
    },
    [captureOpen, captureRole, developerMode, verbose]
  );
  const darkTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: theme.brand,
      },
      secondary: {
        main: theme.accent,
      },
      background: {
        default: theme.background,
      },
    },
  });

  useIonViewWillEnter(() => {
    if (isScannerActive) {
      stopScanner();
    }
  });

  useIonViewDidEnter(() => {
    const matchMedia = window.matchMedia("(prefers-color-scheme: dark)");
    const updateTheme = (e: MediaQueryListEvent) => {
      document.body.classList.toggle("dark", e.matches);
    };

    document.body.classList.toggle("dark", matchMedia.matches);
    matchMedia.addEventListener("change", updateTheme);

    return () => {
      matchMedia.removeEventListener("change", updateTheme);
    };
  });

  const processedCount = useMemo(
    () => Object.values(processedGroups).reduce((sum, items) => sum + items.length, 0),
    [processedGroups]
  );
  const unprocessedCount = useMemo(
    () => Object.values(unprocessedGroups).reduce((sum, items) => sum + items.length, 0),
    [unprocessedGroups]
  );
  const itemCount = processedCount + unprocessedCount;

  const isLocationFullyProcessed = itemCount > 0 && processedCount === itemCount;

  const canShowSignatureTools =
    developerMode && (isLocationFullyProcessed || signatureOverrideActive);

  useEffect(() => {
    if (!canShowSignatureTools && captureOpen) {
      setCaptureOpen(false);
      setCaptureRole(null);
    }
  }, [canShowSignatureTools, captureOpen]);

  const openSignatureModal = useCallback(
    (role: SignatureRole) => {
      logSignatureDebug("openSignatureModal invoked", { role });
      setCaptureRole(role);
      setCaptureOpen(true);
    },
    [logSignatureDebug]
  );

  const closeSignatureModal = useCallback(() => {
    logSignatureDebug("closeSignatureModal invoked");
    setCaptureOpen(false);
    setCaptureRole(null);
  }, [logSignatureDebug]);

  const handleSignatureSaved = useCallback(
    ({ role }: SignatureCaptureResult) => {
      logSignatureDebug("handleSignatureSaved invoked", { role });
      const friendly = role === "driver" ? "Driver" : "Recipient";
      showSnackbar({
        message: `${friendly} signature captured (dev stub).`,
        severity: "success",
        duration: 4000,
      });
      logSignatureDebug("handleSignatureSaved displayed snackbar", { friendly });
      closeSignatureModal();
    },
    [closeSignatureModal, logSignatureDebug, showSnackbar]
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <IonPage>
        <IonHeader>
          <ShipmentViewAppBar position="static" />
        </IonHeader>
        <IonContent>
          <IonRefresher
            slot="fixed"
            onIonRefresh={async (e) => {
              try {
                if (userData?.role === "manufacturer") {
                  await fetchShipmentsByManufacturerId();
                } else if (userData?.role === "driver") {
                  await fetchShipmentsForDriver();
                } else if (userData?.role === "dealer") {
                  await fetchOrdersByDealerId();
                }
              } catch (_) {}
              setTimeout(() => (e.detail as any).complete(), 300);
            }}
          >
            <IonRefresherContent pullingText="Pull to refresh" />
          </IonRefresher>
          <CurrentWorkingList />
        </IonContent>
        {canShowSignatureTools && (
          <>
            <SignatureDevFooter
              onDriverClick={() => openSignatureModal("driver")}
              onRecipientClick={() => openSignatureModal("recipient")}
            />
            <SignatureCaptureModal
              isOpen={captureOpen}
              role={captureRole}
              onDismiss={closeSignatureModal}
              onSave={handleSignatureSaved}
            />
          </>
        )}
      </IonPage>
    </ThemeProvider>
  );
};

export default Tab1;
